import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTenantPath } from '../lib/tenantPath';
import {
  ArrowLeft, ChartPie, TrendUp, TrendDown, Warning, Package, MapPin, User as UserIcon, CurrencyInr, MagnifyingGlass, CaretUp, CaretDown, DownloadSimple,
} from '@phosphor-icons/react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
  BarChart, Bar, Legend,
} from 'recharts';
import useAuthStore from '../store/authStore';
import { exportRowsToCsv } from '../lib/csvExport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const formatMonth = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(mo, 10) - 1]} ${y?.slice(2)}`;
};

// Indian FY: Apr 1 → Mar 31
const fyRange = () => {
  const today = new Date();
  const y = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  const start = new Date(y, 3, 1);
  const isoDay = (d) => d.toISOString().slice(0, 10);
  return { start: isoDay(start), end: isoDay(today), label: `FY ${y}-${String(y + 1).slice(2)}` };
};

const RANGES = [
  { id: 'fytd', label: 'Current FY' },
  { id: '30d', label: 'Last 30d' },
  { id: '90d', label: 'Last 90d' },
  { id: 'all', label: 'All-time' },
];

const resolveRange = (id) => {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const subDays = (n) => { const x = new Date(today); x.setDate(x.getDate() - n + 1); return x; };
  if (id === 'fytd') { const r = fyRange(); return { start: r.start, end: r.end }; }
  if (id === '30d') return { start: iso(subDays(30)), end: iso(today) };
  if (id === '90d') return { start: iso(subDays(90)), end: iso(today) };
  return { start: '', end: '' };
};

const KpiCard = ({ label, value, sub, color = '#00a69c', bg = '#E2F3F0', icon: Icon, testid }) => (
  <div className="medical-card p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: bg, color }}>
        {Icon && <Icon size={16} weight="bold" />}
      </div>
      <span className="text-xs text-[#6D8E87]">{label}</span>
    </div>
    <div className="text-2xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>{value}</div>
    {sub && <div className="text-[11px] text-[#6D8E87] mt-1">{sub}</div>}
  </div>
);

const GrossMargin = () => {
  const navigate = useNavigate();
  const tp = useTenantPath();
  const { user } = useAuthStore();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rangeId, setRangeId] = useState('fytd');
  // iter115 — banner drill-down modal state
  const [excludedOpen, setExcludedOpen] = useState(false);
  const range = useMemo(() => resolveRange(rangeId), [rangeId]);
  const canView = ['Vice President', 'President'].includes(user?.role);
  // Snapshot of the currently applied date filter so the modal fetches
  // exactly the same window as the KPI (counts always agree).
  const filterDates = { start: range.start, end: range.end };

  // Split load() out of useEffect so the modal can trigger a refresh after
  // a successful COGS save (banner count should drop as products are fixed).
  const load = React.useCallback(() => {
    if (!canView) return;
    setLoading(true);
    const params = {};
    if (range.start) params.start_date = range.start;
    if (range.end) params.end_date = range.end;
    axios.get(`${API}/analytics/gross-margin`, { params })
      .then(r => { setData(r.data); setError(null); })
      .catch(e => setError(e.response?.data?.detail || 'Failed to load Gross Margin analytics'))
      .finally(() => setLoading(false));
  }, [range.start, range.end, canView]);

  useEffect(() => { load(); }, [load]);

  if (!canView) {
    return (
      <div className="p-6" data-testid="gm-forbidden">
        <div className="medical-card p-8 text-center">
          <Warning size={48} className="text-[#EF4444] mx-auto mb-3" weight="duotone" />
          <h2 className="text-xl font-bold text-[#052A23] mb-1" style={{ fontFamily: 'Outfit' }}>Executive View</h2>
          <p className="text-sm text-[#6D8E87]">Gross Margin analytics are restricted to Vice President and President.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="p-6 text-[#00a69c]" data-testid="gm-loading">Loading gross margin analytics…</div>;
  }
  if (error) {
    return <div className="p-6 text-[#EF4444]" data-testid="gm-error">{error}</div>;
  }
  if (!data) return null;

  const gmColor = data.gm_pct >= 30 ? '#065F46' : data.gm_pct >= 15 ? '#92400E' : '#991B1B';
  const gmBg = data.gm_pct >= 30 ? '#D1FAE5' : data.gm_pct >= 15 ? '#FEF3C7' : '#FEE2E2';

  return (
    <div className="p-6 space-y-6" data-testid="gross-margin-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(tp('/dashboard'))}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-[#D5E8E4] hover:bg-[#E2F3F0] transition"
            data-testid="gm-back"
          >
            <ArrowLeft size={18} className="text-[#3B5A54]" />
          </button>
          <div>
            <p className="text-xs uppercase tracking-widest text-[#92400E] font-bold">Executive Analytics</p>
            <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>Gross Margin</h1>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid="gm-range-filter">
          {RANGES.map(r => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRangeId(r.id)}
              className={`px-3 py-1.5 text-xs rounded-full font-medium transition ${
                rangeId === r.id
                  ? 'bg-[#052A23] text-white shadow'
                  : 'bg-white border border-[#D5E8E4] text-[#3B5A54] hover:bg-[#E2F3F0]'
              }`}
              data-testid={`gm-range-${r.id}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          label="Revenue (included)"
          value={formatINR(data.total_revenue)}
          sub={`${data.included_items} line item${data.included_items === 1 ? '' : 's'}`}
          color="#00a69c" bg="#E2F3F0" icon={CurrencyInr}
          testid="gm-kpi-revenue"
        />
        <KpiCard
          label="Total COGS"
          value={formatINR(data.total_cogs)}
          sub="Executive-only cost aggregate"
          color="#92400E" bg="#FEF3C7" icon={Package}
          testid="gm-kpi-cogs"
        />
        <KpiCard
          label="Gross Margin ₹"
          value={formatINR(data.gm_amount)}
          sub="Revenue − COGS"
          color={gmColor} bg={gmBg} icon={TrendUp}
          testid="gm-kpi-amount"
        />
        <KpiCard
          label="Gross Margin %"
          value={`${data.gm_pct}%`}
          sub={data.gm_pct >= 30 ? 'Healthy' : data.gm_pct >= 15 ? 'Watch closely' : 'Below target'}
          color={gmColor} bg={gmBg} icon={ChartPie}
          testid="gm-kpi-pct"
        />
      </div>

      {/* Data quality banner — iter115: clickable → opens excluded-items drilldown modal */}
      {(data.excluded_items > 0 || data.excluded_quotes > 0) && (
        <button
          type="button"
          onClick={() => setExcludedOpen(true)}
          className="medical-card p-4 border-l-4 border-[#F59E0B] w-full text-left hover:bg-[#FFFBEB] transition"
          data-testid="gm-excluded-banner"
        >
          <div className="flex items-start gap-3">
            <Warning size={22} weight="fill" className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#92400E]">
                {data.excluded_items} line item{data.excluded_items === 1 ? '' : 's'} across {data.excluded_quotes} quotation{data.excluded_quotes === 1 ? '' : 's'} excluded — COGS not set at time of sale.
              </p>
              <p className="text-xs text-[#6D8E87] mt-1">
                <b className="text-[#00857e] underline underline-offset-2">Click to review and set COGS inline</b> — updating a product's <b>cogs_price</b> backfills every historical line for that product in one shot.
              </p>
            </div>
          </div>
        </button>
      )}

      {excludedOpen && (
        <ExcludedItemsModal
          startDate={filterDates.start}
          endDate={filterDates.end}
          onClose={() => setExcludedOpen(false)}
          onSaved={() => { load(); }}
        />
      )}

      {/* Trend chart */}
      <div className="medical-card p-5" data-testid="gm-trend">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>GM% Trend</h3>
          <span className="text-xs text-[#6D8E87]">Monthly, up to last 24 months</span>
        </div>
        {data.monthly_trend.length === 0 ? (
          <div className="text-sm text-[#6D8E87] py-8 text-center">No approved quotations with COGS in the selected window.</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.monthly_trend.map(m => ({ ...m, monthLabel: formatMonth(m.month) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#D5E8E4" />
              <XAxis dataKey="monthLabel" stroke="#6D8E87" fontSize={11} />
              <YAxis stroke="#6D8E87" fontSize={11} unit="%" domain={[0, 'auto']} />
              <Tooltip
                contentStyle={{ border: '1px solid #D5E8E4', borderRadius: 8, fontSize: 12 }}
                formatter={(v, name) => name === 'gm_pct' ? [`${v}%`, 'GM %'] : [formatINR(v), name === 'revenue' ? 'Revenue' : name === 'cogs' ? 'COGS' : name]}
              />
              <ReferenceLine y={30} stroke="#10B981" strokeDasharray="4 4" label={{ value: 'Healthy 30%', fill: '#065F46', fontSize: 10, position: 'right' }} />
              <ReferenceLine y={15} stroke="#F59E0B" strokeDasharray="4 4" label={{ value: 'Watch 15%', fill: '#92400E', fontSize: 10, position: 'right' }} />
              <Line type="monotone" dataKey="gm_pct" stroke="#00a69c" strokeWidth={3} dot={{ r: 4 }} name="GM %" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Top / Bottom Products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="medical-card p-5" data-testid="gm-top-products">
          <div className="flex items-center gap-2 mb-3">
            <TrendUp size={20} weight="bold" className="text-[#065F46]" />
            <h3 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Top Margin Products</h3>
          </div>
          {data.top_gm_products.length === 0 ? (
            <p className="text-sm text-[#6D8E87] py-6 text-center">No products with COGS yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wide text-[#6D8E87] border-b border-[#D5E8E4]">
                  <tr>
                    <th className="text-left py-2 font-semibold">Product</th>
                    <th className="text-right py-2 font-semibold">Units</th>
                    <th className="text-right py-2 font-semibold">Revenue</th>
                    <th className="text-right py-2 font-semibold">GM %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_gm_products.map((p) => (
                    <tr key={p.product_id} className="border-b border-[#F4F9F7]" data-testid={`gm-top-row-${p.product_id}`}>
                      <td className="py-2 text-[#052A23] font-medium truncate max-w-[220px]">{p.product_name}</td>
                      <td className="py-2 text-right text-[#3B5A54]">{p.units_sold}</td>
                      <td className="py-2 text-right text-[#3B5A54]">{formatINR(p.revenue)}</td>
                      <td className="py-2 text-right font-bold text-[#065F46]">{p.gm_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="medical-card p-5" data-testid="gm-bottom-products">
          <div className="flex items-center gap-2 mb-3">
            <TrendDown size={20} weight="bold" className="text-[#991B1B]" />
            <h3 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Bottom Margin Products</h3>
          </div>
          {data.bottom_gm_products.length === 0 ? (
            <p className="text-sm text-[#6D8E87] py-6 text-center">No products with COGS yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wide text-[#6D8E87] border-b border-[#D5E8E4]">
                  <tr>
                    <th className="text-left py-2 font-semibold">Product</th>
                    <th className="text-right py-2 font-semibold">Units</th>
                    <th className="text-right py-2 font-semibold">Revenue</th>
                    <th className="text-right py-2 font-semibold">GM %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.bottom_gm_products.map((p) => (
                    <tr key={p.product_id} className="border-b border-[#F4F9F7]" data-testid={`gm-bottom-row-${p.product_id}`}>
                      <td className="py-2 text-[#052A23] font-medium truncate max-w-[220px]">{p.product_name}</td>
                      <td className="py-2 text-right text-[#3B5A54]">{p.units_sold}</td>
                      <td className="py-2 text-right text-[#3B5A54]">{formatINR(p.revenue)}</td>
                      <td className={`py-2 text-right font-bold ${p.gm_pct >= 15 ? 'text-[#92400E]' : 'text-[#991B1B]'}`}>{p.gm_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* All Products — Average GM & Qty sold (Feb 2026 request) */}
      <AverageGmByProduct rows={data.product_breakdown} formatINR={formatINR} />

      {/* User + Region breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="medical-card p-5" data-testid="gm-by-user">
          <div className="flex items-center gap-2 mb-3">
            <UserIcon size={20} weight="bold" className="text-[#00a69c]" />
            <h3 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>By Sales Executive</h3>
          </div>
          {data.user_breakdown.length === 0 ? (
            <p className="text-sm text-[#6D8E87] py-6 text-center">No revenue attributed yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(200, data.user_breakdown.length * 40)}>
              <BarChart data={data.user_breakdown.slice(0, 8)} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#D5E8E4" />
                <XAxis type="number" stroke="#6D8E87" fontSize={11} />
                <YAxis type="category" dataKey="name" stroke="#6D8E87" fontSize={11} width={110} />
                <Tooltip
                  contentStyle={{ border: '1px solid #D5E8E4', borderRadius: 8, fontSize: 12 }}
                  formatter={(v, name) => name === 'gm_pct' ? [`${v}%`, 'GM %'] : [formatINR(v), name]}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="gm_amount" fill="#00a69c" name="GM ₹" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="medical-card p-5" data-testid="gm-by-region">
          <div className="flex items-center gap-2 mb-3">
            <MapPin size={20} weight="bold" className="text-[#8B5CF6]" />
            <h3 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>By Region</h3>
          </div>
          {data.region_breakdown.length === 0 ? (
            <p className="text-sm text-[#6D8E87] py-6 text-center">No revenue attributed yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wide text-[#6D8E87] border-b border-[#D5E8E4]">
                  <tr>
                    <th className="text-left py-2 font-semibold">Region</th>
                    <th className="text-right py-2 font-semibold">Quotes</th>
                    <th className="text-right py-2 font-semibold">Revenue</th>
                    <th className="text-right py-2 font-semibold">GM ₹</th>
                    <th className="text-right py-2 font-semibold">GM %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.region_breakdown.map((r) => (
                    <tr key={r.region} className="border-b border-[#F4F9F7]" data-testid={`gm-region-row-${r.region}`}>
                      <td className="py-2 text-[#052A23] font-medium">{r.region}</td>
                      <td className="py-2 text-right text-[#3B5A54]">{r.quotes}</td>
                      <td className="py-2 text-right text-[#3B5A54]">{formatINR(r.revenue)}</td>
                      <td className="py-2 text-right text-[#3B5A54]">{formatINR(r.gm_amount)}</td>
                      <td className={`py-2 text-right font-bold ${r.gm_pct >= 30 ? 'text-[#065F46]' : r.gm_pct >= 15 ? 'text-[#92400E]' : 'text-[#991B1B]'}`}>
                        {r.gm_pct}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GrossMargin;

// ────────────────────────────────────────────────────────────────────────
// Average GM by product — full sortable + searchable list.
// The GM endpoint's `product_breakdown` is already a per-product roll-up
// where `gm_pct` is the revenue-weighted average GM (i.e. Σgm ÷ Σrev
// across every closed line). That's exactly what "average GM at which a
// product is closed" means for financial reporting, so we surface it 1:1.
// ────────────────────────────────────────────────────────────────────────
function AverageGmByProduct({ rows = [], formatINR }) {
  const [q, setQ] = React.useState('');
  const [sortKey, setSortKey] = React.useState('units_sold');
  const [sortDir, setSortDir] = React.useState('desc');   // 'asc' | 'desc'

  const filtered = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filteredRows = needle
      ? rows.filter((r) => (r.product_name || '').toLowerCase().includes(needle))
      : rows.slice();
    filteredRows.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (typeof va === 'string' || typeof vb === 'string') {
        return sortDir === 'asc'
          ? String(va || '').localeCompare(String(vb || ''))
          : String(vb || '').localeCompare(String(va || ''));
      }
      return sortDir === 'asc' ? (va || 0) - (vb || 0) : (vb || 0) - (va || 0);
    });
    return filteredRows;
  }, [rows, q, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Default: numeric fields start desc (biggest first), text ascends.
      setSortDir(typeof (rows[0] || {})[key] === 'string' ? 'asc' : 'desc');
    }
  };

  const SortHeader = ({ label, sk, className = '' }) => (
    <th
      onClick={() => toggleSort(sk)}
      className={`py-2 font-semibold cursor-pointer select-none hover:text-[#00857e] ${className}`}
      data-testid={`gm-avg-sort-${sk}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === sk && (sortDir === 'asc' ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
      </span>
    </th>
  );

  const totalUnits = rows.reduce((s, r) => s + (r.units_sold || 0), 0);
  const totalRevenue = rows.reduce((s, r) => s + (r.revenue || 0), 0);
  const totalGm = rows.reduce((s, r) => s + (r.gm_amount || 0), 0);
  const weightedGm = totalRevenue > 0 ? (totalGm / totalRevenue * 100) : 0;

  return (
    <div className="medical-card p-5" data-testid="gm-avg-by-product">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Package size={20} weight="bold" className="text-[#00a69c]" />
          <h3 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Average GM by Product</h3>
          <span className="text-[10px] uppercase tracking-widest text-[#6D8E87] hidden sm:inline">revenue-weighted</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative w-full sm:w-72">
            <MagnifyingGlass size={14} className="absolute left-3 top-2.5 text-[#6D8E87]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search product…"
              className="w-full pl-8 pr-3 py-1.5 border border-[#D5E8E4] rounded-lg text-sm bg-white"
              data-testid="gm-avg-search"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              // Export the CURRENT (filtered+sorted) view so managers can
              // slice the data before dropping it into a board deck.
              const today = new Date().toISOString().slice(0, 10);
              exportRowsToCsv({
                filename: `avg-gm-by-product-${today}.csv`,
                columns: [
                  { key: 'product_name', label: 'Product' },
                  { key: 'units_sold', label: 'Qty sold' },
                  { key: 'quotes', label: 'Quotes' },
                  { key: 'revenue', label: 'Revenue (INR)', format: (v) => (v ?? 0).toFixed(2) },
                  { key: 'gm_amount', label: 'GM (INR)', format: (v) => (v ?? 0).toFixed(2) },
                  { key: 'gm_pct', label: 'Avg GM %', format: (v) => (v ?? 0).toFixed(2) },
                ],
                rows: filtered,
              });
            }}
            disabled={filtered.length === 0}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#00a69c] text-white hover:bg-[#00857e] disabled:opacity-40 disabled:cursor-not-allowed"
            title={`Export ${filtered.length} row${filtered.length === 1 ? '' : 's'} to CSV`}
            data-testid="gm-avg-export"
          >
            <DownloadSimple size={14} weight="bold" /> Export CSV
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-[#6D8E87] py-6 text-center">
          No closed sales with COGS on file yet — as soon as a Won lead has an approved quote with product COGS, that product will show up here.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wide text-[#6D8E87] border-b border-[#D5E8E4] sticky top-0 bg-white">
                <tr>
                  <SortHeader label="Product" sk="product_name" className="text-left" />
                  <SortHeader label="Qty sold" sk="units_sold" className="text-right" />
                  <SortHeader label="Quotes" sk="quotes" className="text-right" />
                  <SortHeader label="Revenue" sk="revenue" className="text-right" />
                  <SortHeader label="GM ₹" sk="gm_amount" className="text-right" />
                  <SortHeader label="Avg GM %" sk="gm_pct" className="text-right" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const gmClass = p.gm_pct >= 30
                    ? 'text-[#065F46]'
                    : p.gm_pct >= 15
                      ? 'text-[#92400E]'
                      : 'text-[#991B1B]';
                  return (
                    <tr key={p.product_id} className="border-b border-[#F4F9F7] hover:bg-[#F9FCFB]" data-testid={`gm-avg-row-${p.product_id}`}>
                      <td className="py-2 text-[#052A23] font-medium truncate max-w-[260px]" title={p.product_name}>{p.product_name || '—'}</td>
                      <td className="py-2 text-right text-[#052A23] font-semibold">{Number(p.units_sold || 0).toLocaleString('en-IN')}</td>
                      <td className="py-2 text-right text-[#6D8E87]">{p.quotes}</td>
                      <td className="py-2 text-right text-[#3B5A54]">{formatINR(p.revenue)}</td>
                      <td className="py-2 text-right text-[#3B5A54]">{formatINR(p.gm_amount)}</td>
                      <td className={`py-2 text-right font-bold ${gmClass}`}>{Number(p.gm_pct || 0).toFixed(2)}%</td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-[#6D8E87] italic">No products match "{q}".</td>
                  </tr>
                )}
              </tbody>
              <tfoot className="border-t-2 border-[#D5E8E4] bg-[#F4F9F7] sticky bottom-0">
                <tr>
                  <td className="py-2 text-[10px] uppercase tracking-widest text-[#052A23] font-bold">Total ({filtered.length} of {rows.length})</td>
                  <td className="py-2 text-right text-[#052A23] font-bold">{Number(totalUnits).toLocaleString('en-IN')}</td>
                  <td className="py-2 text-right text-[#6D8E87]">—</td>
                  <td className="py-2 text-right text-[#052A23] font-bold">{formatINR(totalRevenue)}</td>
                  <td className="py-2 text-right text-[#052A23] font-bold">{formatINR(totalGm)}</td>
                  <td className="py-2 text-right text-[#052A23] font-bold">{weightedGm.toFixed(2)}%</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="text-[10px] text-[#6D8E87] mt-2 italic">
            Avg GM % = Σ margin ÷ Σ revenue across every closed line for that product. Excludes lines without COGS.
          </p>
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// iter115 — "N line items excluded" drill-down modal.
// Groups excluded line items by product (since setting cogs_price on a
// product cascades to every zero-COGS line via the existing
// PATCH /api/products/{id} backfill). One save fixes N historical rows.
// ────────────────────────────────────────────────────────────────────────
function ExcludedItemsModal({ startDate, endDate, onClose, onSaved }) {
  const [rows, setRows] = React.useState(null);   // null = loading
  const [totals, setTotals] = React.useState({ total_items: 0, total_quotations: 0 });
  const [error, setError] = React.useState('');
  const [expanded, setExpanded] = React.useState({}); // {product_id: bool}
  const [inputs, setInputs] = React.useState({});     // {product_id: string}
  const [savingId, setSavingId] = React.useState(''); // product_id currently saving
  const [savedIds, setSavedIds] = React.useState(new Set());

  const load = React.useCallback(async () => {
    setRows(null);
    setError('');
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const r = await axios.get(`${API}/analytics/gross-margin/excluded-items`, { params });
      setRows(r.data.products || []);
      setTotals({ total_items: r.data.total_items || 0, total_quotations: r.data.total_quotations || 0 });
      // Prefill each input with current cogs_price so a small tweak is a
      // one-click save; empty input if no price is set yet.
      const seed = {};
      (r.data.products || []).forEach((p) => {
        if (p.current_cogs_price > 0) seed[p.product_id] = String(p.current_cogs_price);
      });
      setInputs(seed);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load excluded items');
      setRows([]);
    }
  }, [startDate, endDate]);

  React.useEffect(() => { load(); }, [load]);

  const saveCogs = async (product) => {
    const raw = (inputs[product.product_id] || '').trim();
    const v = Number(raw);
    if (!raw || isNaN(v) || v <= 0) {
      setError(`Enter a COGS > 0 for ${product.product_name}`);
      return;
    }
    setSavingId(product.product_id);
    setError('');
    try {
      const r = await axios.patch(`${API}/products/${product.product_id}`, { cogs_price: v });
      const backfilled = r.data?.backfilled_quotation_items || 0;
      setSavedIds((s) => new Set([...s, product.product_id]));
      // Refetch — the row should now vanish. Also refresh the parent KPI.
      await load();
      onSaved && onSaved(backfilled);
    } catch (e) {
      setError(e.response?.data?.detail || `Failed to save COGS for ${product.product_name}`);
    } finally {
      setSavingId('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="excluded-items-modal" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#E2ECEA] flex items-start justify-between gap-4 bg-[#FFFBEB]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#92400E] font-bold flex items-center gap-1"><Warning size={12} weight="fill" /> Data quality</div>
            <h2 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
              Line items missing COGS
            </h2>
            <p className="text-xs text-[#6D8E87] mt-1">
              <b>{totals.total_items}</b> item{totals.total_items === 1 ? '' : 's'} across <b>{totals.total_quotations}</b> quotation{totals.total_quotations === 1 ? '' : 's'} — grouped by product. Set COGS once and the backfill cascades to every historical hit for that product.
            </p>
          </div>
          <button onClick={onClose} className="text-[#6D8E87] hover:text-[#052A23] text-xl px-2" data-testid="excluded-modal-close" aria-label="Close">×</button>
        </div>

        {error && (
          <div className="px-5 py-2 bg-red-50 border-b border-red-100 text-xs text-red-700" data-testid="excluded-modal-error">{error}</div>
        )}

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {rows === null && <div className="text-sm text-[#6D8E87]">Loading excluded items…</div>}
          {rows !== null && rows.length === 0 && (
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center" data-testid="excluded-modal-empty">
              ✓ No excluded items in this window — every approved line has COGS.
            </div>
          )}
          {(rows || []).map((p) => {
            const isOpen = !!expanded[p.product_id];
            const isSaving = savingId === p.product_id;
            const wasSaved = savedIds.has(p.product_id);
            return (
              <div key={p.product_id} className="border border-[#E2ECEA] rounded-lg overflow-hidden bg-white" data-testid={`excluded-product-${p.product_id}`}>
                <div className="grid grid-cols-12 items-center gap-3 p-3 hover:bg-[#F4F9F7]">
                  <button
                    onClick={() => setExpanded((s) => ({ ...s, [p.product_id]: !s[p.product_id] }))}
                    className="col-span-5 text-left"
                    data-testid={`excluded-product-toggle-${p.product_id}`}
                  >
                    <div className="text-sm font-semibold text-[#052A23]">{p.product_name || '— unnamed —'}</div>
                    <div className="text-[11px] text-[#6D8E87] flex items-center gap-2">
                      {p.product_sku && <span>SKU: <b className="text-[#3B5A54]">{p.product_sku}</b></span>}
                      <span>Selling: <b className="text-[#3B5A54]">{formatINR(p.current_selling_price)}</b></span>
                      <span className="text-[#00857e] font-semibold">{p.affected_items} line{p.affected_items === 1 ? '' : 's'} · {formatINR(p.total_revenue)} rev</span>
                    </div>
                  </button>
                  <div className="col-span-4 flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold whitespace-nowrap">COGS ₹</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={inputs[p.product_id] ?? ''}
                      onChange={(e) => setInputs((s) => ({ ...s, [p.product_id]: e.target.value }))}
                      placeholder={p.current_cogs_price > 0 ? String(p.current_cogs_price) : '0.00'}
                      className="w-full px-3 py-1.5 border border-[#D5E8E4] rounded text-sm focus:outline-none focus:border-[#00a69c]"
                      data-testid={`excluded-cogs-input-${p.product_id}`}
                      disabled={isSaving}
                    />
                  </div>
                  <div className="col-span-3 flex justify-end">
                    <button
                      onClick={() => saveCogs(p)}
                      disabled={isSaving || !inputs[p.product_id]}
                      className="px-3 py-1.5 rounded bg-[#00a69c] text-white text-xs font-semibold hover:bg-[#00857e] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
                      data-testid={`excluded-save-btn-${p.product_id}`}
                    >
                      {isSaving ? 'Saving…' : wasSaved ? '✓ Saved' : `Set & backfill`}
                    </button>
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t border-[#E2ECEA] bg-[#F9FCFB] px-3 py-2" data-testid={`excluded-product-lines-${p.product_id}`}>
                    <div className="text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold mb-1">Affected line items</div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="text-[10px] text-[#6D8E87] uppercase">
                          <tr>
                            <th className="text-left px-2 py-1">Quote #</th>
                            <th className="text-left px-2 py-1">Customer</th>
                            <th className="text-left px-2 py-1">Lead</th>
                            <th className="text-right px-2 py-1">Qty</th>
                            <th className="text-right px-2 py-1">Sell ₹</th>
                            <th className="text-right px-2 py-1">Line revenue ₹</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.line_items.map((li, idx) => (
                            <tr key={`${li.quotation_id}-${idx}`} className="border-t border-[#E2ECEA]" data-testid={`excluded-line-${p.product_id}-${idx}`}>
                              <td className="px-2 py-1 text-[#3B5A54] font-mono">{li.quotation_number || '—'}</td>
                              <td className="px-2 py-1 text-[#052A23]">{li.customer_name || '—'}</td>
                              <td className="px-2 py-1 text-[#3B5A54]">{li.lead_name || '—'}</td>
                              <td className="px-2 py-1 text-right">{li.quantity}</td>
                              <td className="px-2 py-1 text-right">{formatINR(li.price)}</td>
                              <td className="px-2 py-1 text-right font-semibold">{formatINR(li.revenue)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t border-[#E2ECEA] bg-[#F4F9F7] flex items-center justify-between gap-3">
          <span className="text-[11px] text-[#6D8E87]">
            Setting COGS updates the product AND backfills every zero-COGS line for it (respects the "COGS at sale is immutable once set" rule).
          </span>
          <button onClick={onClose} className="px-4 py-1.5 rounded bg-white border border-[#D5E8E4] text-sm text-[#3B5A54] hover:bg-[#E2ECEA]" data-testid="excluded-modal-done">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
